import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import { logError } from "./utils/errorLogging";
import { isDevEnvironment } from "./utils/envUtils";
import { useStore } from "./store";

import LandingPage from "./pages/homepage";
import RolePage from "./pages/jobs/RolePage";
import AssessmentForm from "./components/Candidate/AssessmentForm";
// import AssessmentResults from "./components/HiringManager/AssessmentResults";
import CandidateHome from "./components/Jobs/CandidateHome";
import CompletedSection from "./components/Jobs/CompletedSection";

import AssessmentList from "./components/AssessmentList/AssessmentList";
import JobDetails from "./pages/jobs/JobDetails";
import Layout from "./components/Layout/Layout";
import CandidateLayout from "./components/Layout/CandidateLayout";
import Login from "./pages/auth/Login";
import SignUp from "./pages/auth/Signup";
import ProtectedRoute from "./components/ProtectedRoute/protectedRoute";
import ResetPassword from "./components/Login/ResetPassword";
import SubscriptionPage from "./components/Jobs/SubscriptionPage";
import AdminSignup from "./components/Login/AdminSignup";
import InterviewPage from "./pages/interviewPage";
import ShortUrlRedirect from "./components/ShortUrlRedirect";

import ProfileSettingPage from "./pages/settings/profile";
import CompanySettingPage from "./pages/settings/company";

import OnboardingCompanyPage from "./pages/onboarding/company";
import AccountInactive from "./pages/account/AccountInactive";

import ApplicantSearchPage from "./pages/ApplicantSearchPage";

import AnalyticsPage from "./pages/AnalyticsPage";
import NotFound from "./pages/NotFound";
import ComponentShowcase from "./lib/ui/ComponentShowcase";
import PersonaSwitcher from "./components/DevTools/PersonaSwitcher";
import CandidateDashboard from "./pages/candidate/CandidateDashboard";
import CandidateCompanies from "./pages/candidate/CandidateCompanies";
import CandidateProfile from "./pages/candidate/CandidateProfile";
import CandidateAccount from "./pages/candidate/CandidateAccount";
import CandidatePreferences from "./pages/candidate/CandidatePreferences";

function EmployerRoutes() {
  return (
    <Route path="/" element={<Layout />}>
      <Route index element={<Navigate to="/login" replace />} />

      <Route path="/login" element={
        <ErrorBoundary
          title="Login Error"
          description="Error loading the login page."
          onError={(error, errorInfo) => logError("login_page_error", error, errorInfo)}
        >
          <Login open={true} />
        </ErrorBoundary>
      } />

      <Route path="/reset-password" element={
        <ErrorBoundary
          title="Reset Password Error"
          description="Error loading the password reset page."
          onError={(error, errorInfo) => logError("reset_password_error", error, errorInfo)}
        >
          <ResetPassword open={true} />
        </ErrorBoundary>
      } />

      <Route path="/interview/:id/invite" element={
        <ErrorBoundary
          title="Interview Page Error"
          description="Error loading the interview page."
          onError={(error, errorInfo) => logError("interview_page_error", error, errorInfo)}
        >
          <InterviewPage />
        </ErrorBoundary>
      } />

      <Route path="/i/:shortId" element={
        <ErrorBoundary
          title="Interview Page Error"
          description="Error loading the interview page."
          onError={(error, errorInfo) => logError("interview_page_error", error, errorInfo)}
        >
          <ShortUrlRedirect />
        </ErrorBoundary>
      } />

      <Route path="/register" element={
        <ErrorBoundary
          title="Sign Up Error"
          description="Error loading the sign up page."
          onError={(error, errorInfo) => logError("signup_page_error", error, errorInfo)}
        >
          <SignUp />
        </ErrorBoundary>
      } />

      {isDevEnvironment() && (
        <Route path="/components" element={
          <ErrorBoundary
            title="Component Showcase Error"
            description="Error loading the component showcase."
            onError={(error, errorInfo) => logError("component_showcase_error", error, errorInfo)}
          >
            <ComponentShowcase />
          </ErrorBoundary>
        } />
      )}

      <Route element={<ProtectedRoute />}>
        <Route path="/account/inactive" element={
          <ErrorBoundary
            title="Account inactive"
            description="Unable to load this page."
            onError={(error, errorInfo) => logError("account_inactive_error", error, errorInfo)}
          >
            <AccountInactive />
          </ErrorBoundary>
        } />

        <Route path="/onboarding/company" element={
          <ErrorBoundary
            title="Onboarding Error"
            description="Error loading the company onboarding page."
            onError={(error, errorInfo) => logError("onboarding_company_error", error, errorInfo)}
          >
            <OnboardingCompanyPage />
          </ErrorBoundary>
        } />

        <Route path="/jobs" element={
          <ErrorBoundary
            title="Jobs Error"
            description="Error loading the jobs page."
            onError={(error, errorInfo) => logError("jobs_page_error", error, errorInfo)}
          >
            <RolePage />
          </ErrorBoundary>
        } />

        <Route path="/jobs/:role-name/:id" element={
          <ErrorBoundary
            title="Job Details Error"
            description="Error loading job details."
            actionButton={true}
            actionButtonText="Back to Jobs"
            actionButtonOnClick={() => window.location.href = '/jobs'}
            onError={(error, errorInfo) => logError("job_details_error", error, errorInfo, {
              path: window.location.pathname
            })}
          >
            <JobDetails />
          </ErrorBoundary>
        } />

        <Route path="/jobs/new" element={
          <ErrorBoundary
            title="Create Job Error"
            description="Error loading the job creation page."
            actionButton={true}
            actionButtonText="Back to Jobs"
            actionButtonOnClick={() => window.location.href = '/jobs'}
            onError={(error, errorInfo) => logError("create_job_error", error, errorInfo)}
          >
            <JobDetails />
          </ErrorBoundary>
        } />

        <Route path="/applicant-search" element={
          <ErrorBoundary
            title="Applicant Search Error"
            description="Error loading applicant search page."
            onError={(error, errorInfo) => logError("applicant_search_error", error, errorInfo)}
          >
            <ApplicantSearchPage />
          </ErrorBoundary>
        } />

        <Route path="/assessment-list" element={
          <ErrorBoundary
            title="Evaluation List Error"
            description="Error loading the evaluation list."
            onError={(error, errorInfo) => logError("assessment_list_error", error, errorInfo)}
          >
            <AssessmentList />
          </ErrorBoundary>
        } />

        <Route path="/completed-section" element={
          <ErrorBoundary
            title="Completed Section Error"
            description="Error loading completed section."
            onError={(error, errorInfo) => logError("completed_section_error", error, errorInfo)}
          >
            <CompletedSection />
          </ErrorBoundary>
        } />

        <Route path="/candidate-home" element={
          <ErrorBoundary
            title="Candidate Home Error"
            description="Error loading candidate home."
            onError={(error, errorInfo) => logError("candidate_home_error", error, errorInfo)}
          >
            <CandidateHome />
          </ErrorBoundary>
        } />

        <Route path="/assessment-form" element={
          <ErrorBoundary
            title="Evaluation Form Error"
            description="Error loading the evaluation form."
            onError={(error, errorInfo) => logError("assessment_form_error", error, errorInfo)}
          >
            <AssessmentForm />
          </ErrorBoundary>
        } />

        <Route path="/subscription" element={
          <ErrorBoundary
            title="Subscription Error"
            description="Error loading the subscription page."
            onError={(error, errorInfo) => logError("subscription_page_error", error, errorInfo)}
          >
            <SubscriptionPage />
          </ErrorBoundary>
        } />

        <Route path="/settings/profile" element={
          <ErrorBoundary
            title="Profile Settings Error"
            description="Error loading profile settings."
            onError={(error, errorInfo) => logError("profile_settings_error", error, errorInfo)}
          >
            <ProfileSettingPage />
          </ErrorBoundary>
        } />

        <Route path="/settings/company" element={
          <ErrorBoundary
            title="Company Settings Error"
            description="Error loading company settings."
            onError={(error, errorInfo) => logError("company_settings_error", error, errorInfo)}
          >
            <CompanySettingPage />
          </ErrorBoundary>
        } />

        <Route path="/analytics" element={
          <ErrorBoundary
            title="Analytics Error"
            description="Error loading analytics page."
            onError={(error, errorInfo) => logError("analytics_error", error, errorInfo)}
          >
            <AnalyticsPage />
          </ErrorBoundary>
        } />
      </Route>

      <Route path="*" element={
        <ErrorBoundary
          title="Page Not Found Error"
          description="Error loading the page."
          onError={(error, errorInfo) => logError("not_found_error", error, errorInfo)}
        >
          <NotFound />
        </ErrorBoundary>
      } />
    </Route>
  );
}

function CandidateRoutes() {
  return (
    <Route path="/" element={<CandidateLayout />}>
      <Route index element={<Navigate to="/candidate/dashboard" replace />} />

      <Route path="/login" element={
        <ErrorBoundary
          title="Login Error"
          description="Error loading the login page."
          onError={(error, errorInfo) => logError("login_page_error", error, errorInfo)}
        >
          <Login open={true} />
        </ErrorBoundary>
      } />

      <Route element={<ProtectedRoute />}>
        <Route path="/candidate/dashboard" element={
          <ErrorBoundary
            title="Candidate Dashboard Error"
            description="Error loading the candidate dashboard."
            onError={(error, errorInfo) => logError("candidate_dashboard_error", error, errorInfo)}
          >
            <CandidateDashboard />
          </ErrorBoundary>
        } />

        <Route path="/candidate/companies" element={
          <ErrorBoundary
            title="Candidate Companies Error"
            description="Error loading the companies page."
            onError={(error, errorInfo) => logError("candidate_companies_error", error, errorInfo)}
          >
            <CandidateCompanies />
          </ErrorBoundary>
        } />

        <Route path="/candidate/profile" element={
          <ErrorBoundary
            title="Candidate Profile Error"
            description="Error loading the candidate profile page."
            onError={(error, errorInfo) => logError("candidate_profile_error", error, errorInfo)}
          >
            <CandidateProfile />
          </ErrorBoundary>
        } />

        <Route path="/candidate/account" element={
          <ErrorBoundary
            title="Candidate Account Error"
            description="Error loading the account page."
            onError={(error, errorInfo) => logError("candidate_account_error", error, errorInfo)}
          >
            <CandidateAccount />
          </ErrorBoundary>
        } />

        <Route path="/candidate/preferences" element={
          <ErrorBoundary
            title="Candidate Preferences Error"
            description="Error loading the preferences page."
            onError={(error, errorInfo) => logError("candidate_preferences_error", error, errorInfo)}
          >
            <CandidatePreferences />
          </ErrorBoundary>
        } />
      </Route>

      <Route path="*" element={<Navigate to="/candidate/dashboard" replace />} />
    </Route>
  );
}

function AppRoutes() {
  const activePersona = useStore((state) => state.activePersona);
  const showCandidateView = isDevEnvironment() && activePersona === "candidate";

  return (
    <Router>
      <Routes>
        {showCandidateView ? CandidateRoutes() : EmployerRoutes()}
      </Routes>
      <PersonaSwitcher />
    </Router>
  );
}

export default AppRoutes;
